import React from "react";
import AppIcon from "../AppIcon";
import PopUpMenu from "../PopUpMenu";
import GoogleAuthModal from "../GoogleAuthModal";
import { GUIStyle } from "./style.js";
import { TYPES } from "../../segments/FileSystem";
import { themeColors, FileTypes } from "../../config.js";
import {
  blobToBase64,
  blobToArrayBuffer,
  blobToTxt,
  getFileType,
} from "../../utils.js";
import {
  FolderOpenTwoTone,
  FileTextTwoTone,
  FileImageTwoTone,
  FileWordTwoTone,
  FilePptTwoTone,
  FileExcelTwoTone,
  VideoCameraTwoTone,
  FileMarkdownTwoTone,
  ArrowLeftOutlined,
  PlusOutlined,
  CodeTwoTone,
} from "@ant-design/icons";
import {
  isSignedIn,
  checkFolder,
  createDriveFile,
  getDriveFile,
  listenAuthState,
} from "../../api/gapi";

class FileExplorerGUI extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      currentDirectory: null,
      currentContent: {},
      popUpOn: false,
      popUpPosition: {},
      popUpEntries: [],
      googleModalOn: false,
    };
    this.changeCurrentDirectory = this.changeCurrentDirectory.bind(this);
    this.reloadCurrentContent = this.reloadCurrentContent.bind(this);
    this.createFile = this.createFile.bind(this);
    this.createDirectory = this.createDirectory.bind(this);
    this.setNameOnChange = this.setNameOnChange.bind(this);
    this.setGoogleModalOn = this.setGoogleModalOn.bind(this);
    this.saveName = this.saveName.bind(this);
    this.setPopUp = this.setPopUp.bind(this);
    this.closePopUp = this.closePopUp.bind(this);
    this.handleAppClick = this.handleAppClick.bind(this);
    this.handleAppContextMenu = this.handleAppContextMenu.bind(this);
    this.handleAppDrag = this.handleAppDrag.bind(this);
    this.handleDrop = this.handleDrop.bind(this);
  }

  componentDidMount() {
    this.props.fileSystem.addSaveCallBack(this.reloadCurrentContent);
    if (this.props.root) this.changeCurrentDirectory(this.props.root);

    if (this.props.root === "bin") {
      this.props.fileSystem.setOnRemove(() =>
        this.changeCurrentDirectory(this.props.root)
      );
    }
  }

  setGoogleModalOn(modalOn) {
    this.setState((state) => ({
      ...state,
      googleModalOn: modalOn,
    }));
  }

  changeCurrentDirectory(hash) {
    const fileSystem = this.props.fileSystem;
    const currentDirectory = fileSystem.getDirectory(hash);
    let currentContent = currentDirectory.getChildren();
    currentContent = { ...currentContent };
    for (let key in currentContent) {
      currentContent[key] = {
        metadata: currentContent[key],
        nameOnChange: false,
      };
    }

    this.setState((state) => ({
      ...state,
      currentDirectory,
      currentContent,
    }));
  }

  reloadCurrentContent() {
    let currentContent = this.state.currentDirectory.getChildren();
    currentContent = { ...currentContent };
    for (let key in currentContent) {
      currentContent[key] = {
        metadata: currentContent[key],
        nameOnChange: false,
      };
    }
    this.setState((state) => ({
      ...state,
      popUpOn: false,
      currentContent,
    }));
  }

  createFile() {
    this.props.fileSystem.createNewFile(
      this.state.currentDirectory.getHash(),
      "New File",
      ""
    );
    this.reloadCurrentContent();
  }

  createDirectory() {
    this.props.fileSystem.createNewDirectory(
      this.state.currentDirectory.getHash(),
      "New Folder"
    );
    this.reloadCurrentContent();
  }

  deleteNode(hash) {
    if (this.state.currentDirectory.isBin())
      this.props.fileSystem.deepRemoveNode(hash);
    else
      this.props.fileSystem.removeNode(
        this.state.currentDirectory.getHash(),
        hash
      );
    this.reloadCurrentContent();
  }

  setNameOnChange(key, value) {
    const currentContent = this.state.currentContent;
    currentContent[key].nameOnChange = value;
    this.setState((state) => ({
      ...state,
      currentContent,
    }));
  }

  closePopUp(e) {
    e.preventDefault();
    if (e.type === "click")
      window.removeEventListener("click", this.closePopUp);
    else window.removeEventListener("contextmenu", this.closePopUp);
    this.setState((state) => ({
      ...state,
      popUpOn: false,
    }));
  }

  setPopUp(x, y, entries) {
    this.setState((state) => ({
      ...state,
      popUpOn: true,
      popUpPosition: { x, y },
      popUpEntries: entries,
    }));
  }

  saveName(hash, name, type) {
    if (type === TYPES.FILE) {
      this.props.fileSystem.changeFileName(hash, name);
    } else {
      this.props.fileSystem.changeDirectoryName(hash, name);
    }
    this.reloadCurrentContent();
  }

  handleAppClick(metadata) {
    if (metadata.type === TYPES.DIR) this.changeCurrentDirectory(metadata.hash);
    else {
      switch (getFileType(metadata.ext)) {
        case FileTypes.TXT:
        case FileTypes.JS:
        case FileTypes.XML:
        case FileTypes.CSS:
          this.props.addCurrentFile(metadata.hash);
          break;
        case FileTypes.IMG:
        case FileTypes.VID:
          this.props.addCurrentWindow(metadata.hash);
          break;
        case FileTypes.DOC:
        case FileTypes.PPT:
        case FileTypes.XLS:
          this.props.addCurrentWindow(metadata.hash);
          break;
        default:
          this.props.addCurrentFile(metadata.hash);
          break;
      }
    }
  }

  handleAppDrag(e, hash, parent) {
    e.dataTransfer.setData("hash", hash);
    e.dataTransfer.setData("parent", parent);
  }

  handleDrop = async (e) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      for (let file of files) {
        console.log(file);
        const fileName = file.name.split(".")[0];
        const ext = file.name.split(".")[1];
        const fileType = getFileType(ext);
        if (fileType === FileTypes.UNKNOWN) {
          alert("Cannot recognize the target file");
        } else {
          let data = null;
          let extra = {};
          if (
            fileType === FileTypes.TXT ||
            fileType === FileTypes.XML ||
            fileType === FileTypes.CSS ||
            fileType === FileTypes.JS
          )
            data = await blobToTxt(file);
          else if (fileType === FileTypes.IMG || fileType === FileTypes.VID)
            data = await blobToBase64(file);
          else if (
            fileType === FileTypes.DOC ||
            fileType === FileTypes.XLS ||
            fileType === FileTypes.PPT
          ) {
            const uploadDriveFunc = async () => {
              const res = await checkFolder();
              if (!res.error) {
                const fileRes = await createDriveFile(res.folderId, file);
                if (!fileRes.error) {
                  const urlRes = await getDriveFile(fileRes.fileId);
                  if (!urlRes.error) {
                    data = urlRes.fileUrl;
                    extra = {
                      fileId: urlRes.fileId,
                      onDrive: true,
                    };
                    console.log(extra);
                  }
                }
              }
            };
            if (!isSignedIn()) {
              this.setGoogleModalOn(true);
              listenAuthState(async (signedIn) => {
                if (signedIn) {
                  await uploadDriveFunc();
                  console.log(extra);
                  this.props.fileSystem.createNewFile(
                    this.state.currentDirectory.getHash(),
                    fileName,
                    data,
                    ext,
                    extra
                  );
                  this.reloadCurrentContent();
                }
              });
              return;
            } else {
              await uploadDriveFunc();
            }
          } else {
            data = await blobToTxt(file);
          }

          this.props.fileSystem.createNewFile(
            this.state.currentDirectory.getHash(),
            fileName,
            data,
            ext,
            extra
          );
          this.reloadCurrentContent();
        }
      }
    } else {
      const hash = e.dataTransfer.getData("hash");
      const parent = e.dataTransfer.getData("parent");
      const destination = this.state.currentDirectory.getHash();
      this.props.fileSystem.moveNode(parent, hash, destination);
      this.reloadCurrentContent();
    }
  };

  handleBinContextMenu(e, metadata) {
    window.addEventListener("click", this.closePopUp);
    e.preventDefault();
    e.stopPropagation();
    this.setPopUp(e.clientX, e.clientY, [
      {
        content: "Deep Delete",
        func: () => this.deleteNode(metadata.hash),
      },
    ]);
  }

  handleExplorerContextMenu(e) {
    window.addEventListener("click", this.closePopUp);
    e.preventDefault();
    this.setPopUp(e.clientX, e.clientY, [
      {
        content: "Open Terminal",
        func: () =>
          this.props.addCurrentConsole(this.state.currentDirectory.getHash()),
      },
    ]);
  }

  handleAppContextMenu(e, metadata) {
    window.addEventListener("click", this.closePopUp);
    e.preventDefault();
    e.stopPropagation();
    if (metadata.type === TYPES.DIR) {
      this.setPopUp(e.clientX, e.clientY, [
        {
          content: "Delete",
          func: () => this.deleteNode(metadata.hash),
        },
        {
          content: "Rename",
          func: () => {
            this.setNameOnChange(metadata.hash, true);
          },
        },
        {
          content: "Open with code editor",
          func: () => {
            this.props.setCodeEditorRoot(metadata.hash);
          },
        },
        {
          content: "Open in new window",
          func: () => {
            this.props.openWindow(metadata.hash);
          },
        },
      ]);
    } else if (metadata.type === TYPES.FILE) {
      this.setPopUp(e.clientX, e.clientY, [
        {
          content: "Delete",
          func: () => this.deleteNode(metadata.hash),
        },
        {
          content: "Rename",
          func: () => {
            this.setNameOnChange(metadata.hash, true);
          },
        },
        {
          content: "Open with code editor",
          func: () => {
            this.props.setCodeEditorRoot(
              this.props.fileSystem.getFile(metadata.hash).getParent()
            );
          },
        },
        {
          content: "Open as web app",
          func: () => {
            this.props.addCurrentWindow(metadata.hash);
          },
        },
        {
          content: "Install as web app",
          func: () => {
            this.props.setInstallHash(metadata.hash);
          },
        },
      ]);
    }
  }

  render() {
    let entries = [];
    const currentDirectory = this.state.currentDirectory;
    const currentContent = this.state.currentContent;
    const scale = this.props.systemData.iconSize;

    if (currentDirectory && currentContent !== {}) {
      const parent = currentDirectory.getParent();
      if (parent)
        entries.push(
          <AppIcon
            key={"back_btn"}
            icon={
              <ArrowLeftOutlined
                style={{ fontSize: 30, color: themeColors.red }}
              />
            }
            name={"Back"}
            type={null}
            width={100}
            height={110}
            onClick={(e) => {
              this.changeCurrentDirectory(parent);
            }}
            nameOnChange={false}
            nameChangable={false}
            scale={scale}
          />
        );

      for (let key in currentContent) {
        const metadata = currentContent[key].metadata;
        entries.push(
          <AppIcon
            key={metadata.hash}
            icon={
              metadata.type === TYPES.DIR ? (
                <FolderOpenTwoTone
                  style={{ fontSize: 30 }}
                  twoToneColor={themeColors.yellow}
                />
              ) : getFileType(metadata.ext) === FileTypes.IMG ? (
                <FileImageTwoTone
                  style={{ fontSize: 30 }}
                  twoToneColor={themeColors.yellow}
                />
              ) : getFileType(metadata.ext) === FileTypes.VID ? (
                <VideoCameraTwoTone
                  style={{ fontSize: 30 }}
                  twoToneColor={themeColors.yellow}
                />
              ) : getFileType(metadata.ext) === FileTypes.XML ? (
                <FileMarkdownTwoTone
                  style={{ fontSize: 30 }}
                  twoToneColor={themeColors.yellow}
                />
              ) : getFileType(metadata.ext) === FileTypes.JS ? (
                <CodeTwoTone
                  style={{ fontSize: 30 }}
                  twoToneColor={themeColors.yellow}
                />
              ) : getFileType(metadata.ext) === FileTypes.DOC ? (
                <FileWordTwoTone
                  style={{ fontSize: 30 }}
                  twoToneColor={themeColors.yellow}
                />
              ) : getFileType(metadata.ext) === FileTypes.PPT ? (
                <FilePptTwoTone
                  style={{ fontSize: 30 }}
                  twoToneColor={themeColors.yellow}
                />
              ) : getFileType(metadata.ext) === FileTypes.XLS ? (
                <FileExcelTwoTone
                  style={{ fontSize: 30 }}
                  twoToneColor={themeColors.yellow}
                />
              ) : (
                <FileTextTwoTone
                  style={{ fontSize: 30 }}
                  twoToneColor={themeColors.yellow}
                />
              )
            }
            name={metadata.name}
            type={metadata.type}
            width={100}
            height={110}
            onClick={(e) => {
              if (currentDirectory.getHash() === "bin")
                alert("Cannot open nodes inside the recycle bin!");
              else this.handleAppClick(metadata);
            }}
            onRightClick={(e) => {
              if (currentDirectory.getHash() === "bin")
                this.handleBinContextMenu(e, metadata);
              else this.handleAppContextMenu(e, metadata);
            }}
            draggable={true}
            onDragStart={(e) =>
              this.handleAppDrag(e, metadata.hash, currentDirectory.getHash())
            }
            onDragEnd={(e) => {
              if (e.dataTransfer.dragEffect === "none") {
                console.log("no effect");
              } else {
                this.reloadCurrentContent();
              }
            }}
            nameOnChange={currentContent[key].nameOnChange}
            setNameOnChange={(value) => this.setNameOnChange(key, value)}
            saveName={(value, type) => this.saveName(key, value, type)}
            scale={scale}
          />
        );
      }
      if (currentDirectory.getHash() !== "bin")
        entries.push(
          <AppIcon
            key={"create_btn"}
            icon={
              <PlusOutlined
                style={{ fontSize: 30, color: themeColors.green }}
              />
            }
            name={"Create"}
            width={100}
            height={110}
            onClick={(e) => {
              e.stopPropagation();
              window.addEventListener("click", this.closePopUp);
              this.setPopUp(e.clientX, e.clientY, [
                { content: "Create File", func: this.createFile },
                { content: "Create Folder", func: this.createDirectory },
              ]);
            }}
            nameChangable={false}
            nameOnChange={false}
            scale={scale}
          />
        );
    }

    return (
      <div
        style={{
          ...GUIStyle.section,
          width:
            Math.floor(
              (this.props.dims.width * 0.9) / (100 * scale ? scale : 1)
            ) * (100 * scale ? scale : 1),
          ...this.props.style,
        }}
        onDragOver={(e) => {
          e.preventDefault();
        }}
        onDrop={(e) => this.handleDrop(e)}
        onContextMenu={(e) => this.handleExplorerContextMenu(e)}
      >
        {this.state.googleModalOn && (
          <GoogleAuthModal setModalOn={this.setGoogleModalOn} />
        )}
        {currentDirectory ? (
          currentDirectory.getHash() === "root" ? (
            <div style={GUIStyle.title}>{"File Explorer"}</div>
          ) : currentDirectory.getHash() === "bin" ? (
            <div style={GUIStyle.title}>{"Recycle Bin"}</div>
          ) : (
            <div style={GUIStyle.title}>{currentDirectory.getName()}</div>
          )
        ) : (
          <></>
        )}
        <div style={GUIStyle.appBar}>
          {this.state.popUpOn && (
            <PopUpMenu
              top={this.state.popUpPosition.y}
              left={this.state.popUpPosition.x}
              entries={this.state.popUpEntries}
            />
          )}
          {entries}
        </div>
      </div>
    );
  }
}

export default FileExplorerGUI;
